/**
 * ModalShell - THE frame for a self-rendered modal (S2).
 *
 * GlobalModal covers the message/confirm modals and custom types registered
 * with it. Larger modals that own their state (pickers, wizards, uploaders)
 * render themselves - and each of them used to hand-roll the same pieces: a
 * `fixed inset-0` layer, a backdrop, a panel, Escape handling (or not), a close
 * button somewhere. They drifted: black scrims, solid white panels, no focus
 * trap, three different header layouts.
 *
 * This is that frame, once:
 *   - the quiet-glass backdrop `bg-ink/20 backdrop-blur-[2px]` (CLAUDE.md §12.2)
 *   - a `.glass` panel on the width scale, capped at 90vh, body scrolls
 *   - header (icon, title, description, close), body, optional footer
 *   - Headless UI Dialog: focus trap, Escape, scroll lock, aria-modal,
 *     labelled by the title - the same base GlobalModal uses
 *
 * A modal keeps its own content and logic and swaps only its outer frame:
 *
 *   <ModalShell isOpen={open} onClose={close} title={t('Create table')}
 *               icon={<PiTableLight />} size="lg" footer={<Buttons/>}>
 *     ...body...
 *   </ModalShell>
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiXLight } from 'react-icons/pi';

export type ModalShellSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

const SIZE: Record<ModalShellSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  '2xl': 'sm:max-w-5xl',
  full: 'sm:max-w-7xl',
};

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Headline beside the icon. Also the dialog's accessible name. */
  title?: React.ReactNode;
  /** Muted line under the title. */
  description?: React.ReactNode;
  icon?: React.ReactNode;
  /** Right side of the header, before the close button. */
  headerActions?: React.ReactNode;
  /** Pinned to the bottom; the body scrolls between header and footer. */
  footer?: React.ReactNode;
  size?: ModalShellSize;
  /** `tall` fixes the panel at 85vh - for split layouts that fill it. */
  height?: 'auto' | 'tall';
  /** Clicking the backdrop closes. Off for forms with unsaved input. */
  closeOnBackdrop?: boolean;
  hideCloseButton?: boolean;
  /** Body layout. Default `overflow-y-auto px-6 py-5`; a split layout that
      scrolls its own columns passes e.g. `flex p-0 overflow-hidden`. */
  bodyClassName?: string;
  panelClassName?: string;
  /** Accessible name when there is no visible title. */
  ariaLabel?: string;
  /** Above the side panel and GlobalModal's base layer by default. */
  zIndexClassName?: string;
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
  'data-testid'?: string;
}

const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  children,
  title,
  description,
  icon,
  headerActions,
  footer,
  size = 'md',
  height = 'auto',
  closeOnBackdrop = true,
  hideCloseButton = false,
  bodyClassName = 'overflow-y-auto px-6 py-5',
  panelClassName,
  ariaLabel,
  zIndexClassName = 'z-[3100]',
  initialFocus,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const hasHeader = !!(title || icon || description || headerActions || !hideCloseButton);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={clsx('relative', zIndexClassName)}
        // Headless UI calls this for Escape and outside clicks; outside
        // clicks are dropped when the backdrop shouldn't close.
        onClose={closeOnBackdrop ? onClose : () => undefined}
        initialFocus={initialFocus}
        aria-label={!title ? ariaLabel : undefined}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-pop duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid={testId}
                className={clsx(
                  'glass relative flex w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl text-start shadow-xl',
                  SIZE[size],
                  height === 'tall' ? 'h-[85vh]' : 'max-h-[90vh]',
                  panelClassName,
                )}
              >
                {hasHeader && (
                  <div className="flex flex-shrink-0 items-start gap-3 border-b border-hairline-soft px-6 py-4">
                    {icon && (
                      <span className="mt-0.5 flex-shrink-0 text-fg-muted [&>svg]:h-5 [&>svg]:w-5" aria-hidden>
                        {icon}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      {title && (
                        <Dialog.Title as="h2" className="text-base font-medium text-fg-strong">
                          {title}
                        </Dialog.Title>
                      )}
                      {description && (
                        <Dialog.Description as="p" className="mt-0.5 text-sm text-fg-muted">
                          {description}
                        </Dialog.Description>
                      )}
                    </div>
                    {headerActions && <div className="flex flex-shrink-0 items-center gap-2">{headerActions}</div>}
                    {!hideCloseButton && (
                      <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('Close')}
                        className="-me-1 flex-shrink-0 rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
                      >
                        <PiXLight className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                )}

                <div className={clsx('min-h-0 flex-1', bodyClassName)}>
                  {children}
                </div>

                {footer && (
                  <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-hairline-soft px-6 py-3">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalShell;
